import { Box, FormControlLabel, Grid, Switch } from '@mui/material';
import { GridColumn as Column } from '@progress/kendo-react-grid';
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  DeleteButton,
  PrimaryButton,
  SecondaryButton,
  TertiaryButton
} from 'src/components/shared/Button/Button';
import { DataGrid } from 'src/components/shared/DataGrid/DataGrid';
import { DropdownCell } from 'src/components/shared/DataGrid/EditableGrid/DropdownCell';
import { EditableGrid } from 'src/components/shared/DataGrid/EditableGrid/EditableGrid';
import { Autocomplete } from 'src/components/shared/Forms/Autocomplete/Autocomplete';
import { StatusCircle } from 'src/components/shared/StatusCircle/StatusCircle';
import { LeadType } from 'src/components/shared/Typography/LeadType';
import { InstrumentDto } from 'src/store/instruments/instrument';
import { selectActiveInstruments } from 'src/store/instruments/instruments.selectors';
import { selectMethods } from 'src/store/methods/methods.selectors';
import { selectActiveParameters } from 'src/store/parameters/parameters.selectors';
import { ProductDetailDto, ProductStatus } from 'src/store/products/product';
import {
  getCorrectedValue,
  getParameterValue,
  isMaxCustomerOk,
  isMaxProductionOk,
  isMinCustomerOk,
  isMinProductionOk,
  isParameterAdded,
  isParameterCustomerOk,
  isParameterOk,
  isParameterProductionOk
} from 'src/store/products/product.utils';
import {
  ProductParameterDetailDto,
  ProductParameterDto
} from 'src/store/products/productParameter';
import {
  SpecificationDto,
  SpecificationParameterDto
} from 'src/store/specifications/specification';
import { NoSpinNumericCell } from 'src/utils/cell';
import { RoundAwayFromZero } from 'src/utils/math';
import { useAuth } from '../../Auth/AuthContext';
import { AnalysisStatusCell } from './AnalysisStatusCell';
import { AnalysisStatusCircleCell } from './AnalysisStatusCircleCell';

type Props = {
  product: Nullable<ProductDetailDto>;
  productParameters: ProductParameterDetailDto[];
  specification: Nullable<SpecificationDto>;
  specifications: SpecificationDto[];
  specificationParameters: SpecificationParameterDto[];
  onSpecificationChange: (spec: Nullable<SpecificationDto>) => Promise<void>;
  onEditSpecification: (spec: SpecificationDto) => Promise<void>;
  onSaveProductParameters: (
    parameters: ProductParameterDetailDto[]
  ) => Promise<void>;
};

let uniqueId = 0;

export const Analyzes: React.FC<Props> = ({
  product,
  productParameters,
  specification,
  specifications,
  specificationParameters,
  onSpecificationChange,
  onEditSpecification,
  onSaveProductParameters
}) => {
  const { t } = useTranslation();

  const [editedProductParameters, setEditedProductParameters] = useState<
    ProductParameterDetailDto[]
  >([]);
  const [viewCorrectionPartUsed, setViewPartCorrectionUsed] =
    useState<boolean>(false);

  useEffect(() => {
    setEditedProductParameters(productParameters.slice());
  }, [productParameters]);

  const parameters$ = useSelector(selectActiveParameters);
  const methods$ = useSelector(selectMethods);
  const instruments$ = useSelector(selectActiveInstruments);
  const { user } = useAuth();

  const onCancel = () => {
    setEditedProductParameters([...productParameters]);
  };

  const onSaveParameters = () => {
    onSaveProductParameters(editedProductParameters);
  };

  const onAddParameter = () => {
    const productParameter: ProductParameterDetailDto = {
      id: --uniqueId,
      productId: product?.id,
      norlab: false,
      userId: user?.id,
      userInitials: user?.initials
    };
    setEditedProductParameters([productParameter, ...editedProductParameters]);
  };

  const onDeleteParameter = (productParameter: ProductParameterDto) => {
    setEditedProductParameters(
      editedProductParameters.filter(p => p.id !== productParameter.id)
    );
  };

  const onInstrumentChange = (
    instrument: InstrumentDto,
    productParameter: ProductParameterDto
  ) => {
    setEditedProductParameters(
      editedProductParameters.map(pp => {
        if (pp.id === productParameter.id)
          return { ...pp, instrumentId: instrument.id };
        const index = instrument.instrumentParameters.findIndex(
          ip => pp.parameterId === ip.parameterId && pp.methodId === ip.methodId
        );
        return index > -1
          ? {
              ...pp,
              instrumentId: instrument.instrumentParameters[index].instrumentId
            }
          : pp;
      })
    );
  };

  const onPSP = () => {
    const newParameters = specificationParameters
      .filter(
        sp =>
          !editedProductParameters.some(
            pp =>
              pp.parameterId === sp.parameterId && pp.methodId === sp.methodId
          )
      )
      .map(
        sp =>
          ({
            id: --uniqueId,
            productId: product?.id,
            norlab: false,
            userId: user?.id,
            userInitials: user?.initials,
            parameterId: sp.parameterId,
            methodId: sp.methodId,
            minProd: sp.minProd,
            maxProd: sp.maxProd,
            minCustomer: sp.minCustomer,
            maxCustomer: sp.maxCustomer,
            decimalProd: sp.decimalProd,
            decimalCustomer: sp.decimalCustomer
          } as ProductParameterDetailDto)
      );

    setEditedProductParameters([...editedProductParameters, ...newParameters]);
  };

  const onViewCorrectionPartUsed = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => setViewPartCorrectionUsed(event.target.checked);

  const isSpecificationParametersPresent = useMemo(() => {
    return specificationParameters.every(sp =>
      editedProductParameters.some(
        pp =>
          pp.parameterId === sp.parameterId &&
          pp.methodId === sp.methodId &&
          pp.measuredValue != null
      )
    );
  }, [editedProductParameters, specificationParameters]);

  const isProductionOk = useMemo(() => {
    return (
      editedProductParameters.every(pp =>
        isParameterProductionOk(getCorrectedValue(pp), pp)
      ) && isSpecificationParametersPresent
    );
  }, [editedProductParameters, isSpecificationParametersPresent]);

  const isCustomerOk = useMemo(() => {
    return (
      editedProductParameters.every(pp =>
        isParameterCustomerOk(getCorrectedValue(pp), pp)
      ) && isSpecificationParametersPresent
    );
  }, [editedProductParameters, isSpecificationParametersPresent]);

  const productStatus = product?.productStatusId;
  const isInProduction = productStatus === ProductStatus.InProduction;
  const isApproved =
    productStatus === ProductStatus.Approved ||
    productStatus === ProductStatus.ApprovedWithDeviation;

  const ProductParametersList = useMemo(() => {
    return (
      <EditableGrid
        items={editedProductParameters}
        onItemsChange={items => setEditedProductParameters(items)}
      >
        <Column
          width="50px"
          locked={true}
          cell={props => (
            <AnalysisStatusCircleCell
              {...props}
              value={getCorrectedValue(props.dataItem)}
              specification={specification}
            />
          )}
        />
        <Column
          field="parameterId"
          title={t('parameter.singular')}
          width="300px"
          cell={props => (
            <DropdownCell
              {...props}
              disabled={isApproved}
              items={parameters$}
              uniqueField="id"
              optionField="name"
            />
          )}
        />
        <Column
          field="methodId"
          title={t('method.singular')}
          width="220px"
          cell={props => (
            <DropdownCell
              {...props}
              disabled={isApproved}
              items={methods$.filter(m =>
                m.parameterIds.includes(props.dataItem.parameterId)
              )}
              uniqueField="id"
              optionField="name"
            />
          )}
        />
        <Column
          field="instrumentId"
          title={t('instrument.singular')}
          width="220px"
          cell={props => (
            <DropdownCell
              {...props}
              disabled={isApproved}
              items={instruments$.filter(i =>
                i.methodIds.includes(props.dataItem.methodId)
              )}
              uniqueField="id"
              optionField="name"
              onChange={(options: any) => {
                onInstrumentChange(options.selectedItem, options.dataItem);
              }}
            />
          )}
        />

        {isInProduction ? (
          <Column
            field="measuredValue"
            width="140px"
            title={t('parameter.measuredValue')}
            cell={NoSpinNumericCell}
          />
        ) : (
          <Column
            field="measuredValue"
            width="140px"
            title={t('parameter.measuredValue')}
            editable={false}
            cell={props => {
              return (
                <td style={{ textAlign: 'right' }}>
                  {props.dataItem.measuredValue !== null
                    ? props.dataItem.measuredValue
                    : ''}
                </td>
              );
            }}
          />
        )}

        <Column
          field="unit"
          width="100px"
          title={t('props.unit')}
          editable={false}
        />
        {viewCorrectionPartUsed && (
          <Column
            field="correctionPart"
            width="120px"
            title={t('parameter.correctionPartUsed')}
            editable={false}
            cell={props => {
              const decimal =
                props.dataItem.decimalProd !== null ||
                props.dataItem.decimalCustomer !== null
                  ? Math.max(
                      props.dataItem.decimalProd ?? 0,
                      props.dataItem.decimalCustomer ?? 0
                    )
                  : 3;
              return (
                <td style={{ textAlign: 'right' }}>
                  {props.dataItem.correctionPart?.toFixed(decimal)}
                </td>
              );
            }}
          />
        )}
        <Column
          field="instrumentCorrectionPart"
          width="120px"
          title={t('parameter.correctionPartCurrent')}
          editable={false}
          cell={props => {
            const decimal =
              props.dataItem.decimalProd !== null ||
              props.dataItem.decimalCustomer !== null
                ? Math.max(
                    props.dataItem.decimalProd ?? 0,
                    props.dataItem.decimalCustomer ?? 0
                  )
                : 3;
            return (
              <td style={{ textAlign: 'right' }}>
                {props.dataItem.instrumentCorrectionPart?.toFixed(decimal)}
              </td>
            );
          }}
        />
        <Column
          field="lessThan"
          title=" "
          width="70px"
          cell={props => (
            <td style={{ textAlign: 'right' }}>
              {props.dataItem.lessThan && <span className="mr-8">&lt;</span>}
            </td>
          )}
        />
        <Column
          width="120px"
          title={t('parameter.correctedValue')}
          cell={({ dataItem }: { dataItem: ProductParameterDetailDto }) => {
            const value = getCorrectedValue(dataItem);
            const background =
              value === null
                ? 'transparent'
                : isParameterOk(value, dataItem) && specification
                ? 'var(--green-status-color)'
                : 'var(--red-status-color)';
            return (
              <td
                style={{
                  background: background,
                  textAlign: 'right'
                }}
              >
                {value !== null ? RoundAwayFromZero(value, 4) : ''}
              </td>
            );
          }}
        />
        <Column
          field="minProd"
          width="120px"
          title={t('props.minProd')}
          editable={false}
          cell={props => (
            <AnalysisStatusCell
              {...props}
              decimals={props.dataItem.decimalProd}
              ok={isMinProductionOk(props.dataItem)}
            />
          )}
        />
        <Column
          field="maxProd"
          width="120px"
          title={t('props.maxProd')}
          editable={false}
          cell={props => (
            <AnalysisStatusCell
              {...props}
              decimals={props.dataItem.decimalProd}
              ok={isMaxProductionOk(props.dataItem)}
            />
          )}
        />
        <Column
          field="minCustomer"
          width="120px"
          title={t('props.minCustomer')}
          editable={false}
          cell={props => (
            <AnalysisStatusCell
              {...props}
              decimals={props.dataItem.decimalCustomer}
              ok={isMinCustomerOk(props.dataItem)}
            />
          )}
        />
        <Column
          field="maxCustomer"
          width="120px"
          title={t('props.maxCustomer')}
          editable={false}
          cell={props => (
            <AnalysisStatusCell
              {...props}
              decimals={props.dataItem.decimalCustomer}
              ok={isMaxCustomerOk(props.dataItem)}
            />
          )}
        />
        <Column
          field="userInitials"
          width="160px"
          title={t('user.singular')}
          editable={false}
        />
        {isInProduction && (
          <Column
            filterable={false}
            editable={false}
            width="80px"
            cell={props => (
              <td style={{ textAlign: 'right' }}>
                <DeleteButton
                  onClick={() => {
                    onDeleteParameter(props.dataItem);
                  }}
                />
              </td>
            )}
          />
        )}
      </EditableGrid>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    editedProductParameters,
    parameters$,
    methods$,
    instruments$,
    t,
    viewCorrectionPartUsed
  ]);

  const SpecificationParametersList = useMemo(() => {
    return (
      <DataGrid items={specificationParameters} filterable={false}>
        <Column
          width="50px"
          locked={true}
          cell={props => (
            <AnalysisStatusCircleCell
              {...props}
              value={getParameterValue(editedProductParameters, props.dataItem)}
              exists={isParameterAdded(editedProductParameters, props.dataItem)}
              specification={specification}
            />
          )}
        />
        <Column
          field="parameterName"
          title={t('parameter.singular')}
          width="220px"
        />
        <Column field="methodName" title={t('method.singular')} width="220px" />

        <Column
          field="minProd"
          title={t('props.minProd')}
          width="160px"
          cell={props => (
            <td style={{ textAlign: 'right' }}>
              {props.dataItem.minProd?.toFixed(props.dataItem.decimalProd ?? 3)}
            </td>
          )}
        />
        <Column
          field="maxProd"
          title={t('props.maxProd')}
          width="160px"
          cell={props => (
            <td style={{ textAlign: 'right' }}>
              {props.dataItem.maxProd?.toFixed(props.dataItem.decimalProd ?? 3)}
            </td>
          )}
        />
        <Column
          field="minCustomer"
          title={t('props.minCustomer')}
          width="160px"
          cell={props => (
            <td style={{ textAlign: 'right' }}>
              {props.dataItem.minCustomer?.toFixed(
                props.dataItem.decimalCustomer ?? 3
              )}
            </td>
          )}
        />
        <Column
          field="maxCustomer"
          title={t('props.maxCustomer')}
          width="160px"
          cell={props => (
            <td style={{ textAlign: 'right' }}>
              {props.dataItem.maxCustomer?.toFixed(
                props.dataItem.decimalCustomer ?? 3
              )}
            </td>
          )}
        />
      </DataGrid>
    );
  }, [specificationParameters, specification, editedProductParameters, t]);

  return (
    <Fragment>
      <Grid container>
        <Grid item md={12}>
          <Grid container>
            <Grid item xs={12} lg={6}>
              <PrimaryButton
                disabled={isApproved}
                icon="la-save"
                onClick={onSaveParameters}
                className="mr-24"
              >
                {t('save.verb')} {t('parameter.plural')}
              </PrimaryButton>
              <SecondaryButton
                disabled={isApproved}
                icon="la-plus"
                onClick={onAddParameter}
                className="mr-24"
              >
                {t('add.verb')}
              </SecondaryButton>
              <TertiaryButton disabled={isApproved} onClick={onCancel}>
                {t('undo')}
              </TertiaryButton>
            </Grid>
            <Grid item xs={12} lg={6}>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="flex-end"
                height="100%"
                marginBottom="2.5rem"
                paddingRight="1.5rem"
              >
                <FormControlLabel
                  className="mr-40"
                  control={
                    <Switch
                      checked={viewCorrectionPartUsed}
                      onChange={event => {
                        onViewCorrectionPartUsed(event);
                      }}
                      name="viewCorrectionPartUsed"
                    />
                  }
                  label={t('parameter.viewCorrectionPartUsed')}
                />
                <LeadType className="mr-12">{t('production')}</LeadType>
                <StatusCircle
                  color={
                    isProductionOk
                      ? 'var(--green-status-color-rgb)'
                      : 'var(--red-status-color-rgb)'
                  }
                />
                <LeadType className="ml-24 mr-12">
                  {t('customer.singular')}
                </LeadType>
                <StatusCircle
                  color={
                    isCustomerOk
                      ? 'var(--green-status-color-rgb)'
                      : 'var(--red-status-color-rgb)'
                  }
                />
              </Box>
            </Grid>
          </Grid>

          {ProductParametersList}
        </Grid>
        <Grid item md={12}>
          <Box display="flex" alignItems="center" marginY="1.5rem">
            <Autocomplete
              disabled={isApproved}
              style={{ width: 440, marginBottom: 0, marginRight: 24 }}
              options={specifications}
              label={t('specification.plural')}
              optionField="name"
              value={specification}
              renderOption={(props, option) => (
                <li {...props}>
                  {option.no} - {option.name}
                </li>
              )}
              onChange={specification => onSpecificationChange(specification)}
            />
            <PrimaryButton
              className="mr-24"
              disabled={isApproved || !specification}
              onClick={() => {
                onEditSpecification(specification!);
              }}
            >
              {t('product.setSpecification')}
            </PrimaryButton>
            <SecondaryButton
              icon="la-list"
              disabled={isApproved || !specification}
              onClick={onPSP}
              className="mr-24"
            >
              {t('specification.psp')}
            </SecondaryButton>
          </Box>

          {SpecificationParametersList}
        </Grid>
      </Grid>
    </Fragment>
  );
};
